import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { track } from '@vercel/analytics';
import { supabase } from '../lib/supabase';
import { useSession } from '../hooks/useSession';
import Header from './Header';
import Footer from './Footer';

interface ProfileRow {
  slug: string;
  display_name: string;
}

interface Status {
  type: 'success' | 'error';
  message: string;
}

const AccountPage: React.FC = () => {
  const { session, signOut } = useSession();
  const userId = session?.user.id ?? null;
  const email = session?.user.email ?? '';

  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [profileStatus, setProfileStatus] = useState<Status | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [passwordStatus, setPasswordStatus] = useState<Status | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const [signOutStatus, setSignOutStatus] = useState<Status | null>(null);

  useEffect(() => {
    if (!userId) return;
    let mounted = true;
    (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('slug, display_name')
        .eq('user_id', userId)
        .maybeSingle();
      if (!mounted) return;
      if (error) {
        setProfileStatus({ type: 'error', message: error.message });
        return;
      }
      if (data) {
        const row = data as ProfileRow;
        setProfile(row);
        setDisplayName(row.display_name);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [userId]);

  const handleProfileSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!userId) return;
    setProfileStatus(null);
    setProfileSaving(true);
    const trimmedName = displayName.trim();
    const { error } = await supabase
      .from('profiles')
      .update({
        display_name: trimmedName,
      })
      .eq('user_id', userId);
    setProfileSaving(false);
    if (error) {
      setProfileStatus({ type: 'error', message: error.message });
      return;
    }
    setProfile((prev) => (prev ? { ...prev, display_name: trimmedName } : prev));
    track('Profile Updated');
    setProfileStatus({ type: 'success', message: 'Profile updated.' });
  };

  const handlePasswordChange = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPasswordStatus(null);
    setPasswordSaving(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setPasswordSaving(false);
    if (error) {
      setPasswordStatus({ type: 'error', message: error.message });
      return;
    }
    setNewPassword('');
    setPasswordStatus({ type: 'success', message: 'Password updated.' });
  };

  const handleSignOut = async () => {
    setSignOutStatus(null);
    const { error } = await signOut();
    if (error) {
      setSignOutStatus({ type: 'error', message: error.message });
    }
  };

  const profileDirty =
    profile !== null && displayName.trim() !== profile.display_name;

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Header backTo="/" backLabel="All Restaurants" />
      <main className="flex-1 container mx-auto px-6 pt-12 pb-20 max-w-2xl">
        <h1 className="font-display text-4xl md:text-5xl text-charcoal mb-6 tracking-tight">
          Account
        </h1>

        <section className="card p-6 mb-6">
          <h2 className="font-display text-xl text-charcoal mb-2">Email</h2>
          <p className="text-charcoal-light">{email}</p>
        </section>

        <form onSubmit={handleProfileSave} className="card p-6 mb-6 space-y-4">
          <h2 className="font-display text-xl text-charcoal">Profile</h2>
          <label className="block text-sm">
            <span className="text-charcoal-light">Your name</span>
            <input
              type="text"
              required
              maxLength={60}
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full mt-1 px-4 py-3 bg-cream-warm border border-black/10 rounded-lg text-charcoal focus:outline-none focus:border-amber"
            />
            <span className="text-xs text-charcoal-light/80">
              Shown publicly on your recommender page.
            </span>
          </label>
          {profile && (
            <p className="text-xs text-charcoal-light">
              Public URL:{' '}
              <Link
                to={`/recommenders/${profile.slug}`}
                className="font-mono text-amber hover:underline"
              >
                /recommenders/{profile.slug}
              </Link>
              {' '}<span className="text-charcoal-light/70">(contact us to change the URL slug)</span>
            </p>
          )}
          {profileStatus && (
            <p
              className={`text-sm ${
                profileStatus.type === 'success' ? 'text-amber' : 'text-terracotta'
              }`}
              role="status"
            >
              {profileStatus.message}
            </p>
          )}
          <button
            type="submit"
            disabled={profileSaving || !profileDirty || displayName.trim() === ''}
            className="btn-primary disabled:opacity-50"
          >
            {profileSaving ? 'Saving…' : 'Save profile'}
          </button>
        </form>

        <form onSubmit={handlePasswordChange} className="card p-6 mb-6 space-y-4">
          <h2 className="font-display text-xl text-charcoal">Change password</h2>
          <label className="block text-sm">
            <span className="text-charcoal-light">New password</span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full mt-1 px-4 py-3 bg-cream-warm border border-black/10 rounded-lg text-charcoal focus:outline-none focus:border-amber"
            />
          </label>
          {passwordStatus && (
            <p
              className={`text-sm ${
                passwordStatus.type === 'success' ? 'text-amber' : 'text-terracotta'
              }`}
              role="status"
            >
              {passwordStatus.message}
            </p>
          )}
          <button
            type="submit"
            disabled={passwordSaving || newPassword.length < 8}
            className="btn-primary disabled:opacity-50"
          >
            {passwordSaving ? 'Updating…' : 'Update password'}
          </button>
        </form>

        <section className="card p-6">
          <h2 className="font-display text-xl text-charcoal mb-3">Sign out</h2>
          <button
            type="button"
            onClick={handleSignOut}
            className="text-sm text-charcoal-light hover:text-amber underline"
          >
            Sign out of Rankum
          </button>
          {signOutStatus && (
            <p className="mt-2 text-sm text-terracotta" role="status">
              {signOutStatus.message}
            </p>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AccountPage;
